<script lang="ts" setup>
  import { validate as isUUIDValid } from 'uuid'

  import { computed, watch } from 'vue'

  import { useServiceLaunch } from '#ourahub-intra/composables/serviceLaunch'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useAssistantStore } from '#ourahub-intra/stores/assistant'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()
  const assistantStore = useAssistantStore()

  const serviceLaunch = useServiceLaunch()

  const moreCategoriesCount = computed(() => {
    return (
      servicesStore.sortedCategories.slice(5).filter((c) => ouraHubStore.selectedCategories.includes(c.name)).length +
      (ouraHubStore.showUnderDevel ? 1 : 0)
    )
  })

  watch(
    [() => ouraHubStore.searchOrTask, () => servicesStore.servicesList],
    () => {
      ouraHubStore.searchActions = []

      const searchText = ouraHubStore.searchOrTask.trim()

      const publicServices = servicesStore.servicesList.filter((service) => Boolean(service.public))

      const searchType = isUUIDValid(searchText)
        ? 'uuid'
        : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchText)
          ? 'email'
          : /^(?=(?:.*?[A-Za-z]){1,6})(?=(?:.*?[0-9]){8,})[A-Za-z0-9,]{14,16}$/.test(searchText)
            ? 'serial'
            : 'keyword'

      if (searchType !== 'keyword') {
        publicServices.forEach((service) =>
          service.actions?.forEach((action) => {
            if (action.type === searchType) {
              ouraHubStore.searchActions.push({
                ...service,
                id: service.id,
                url: `${action.url}${searchText}`,
                summary: action.name || service.summary,
              })
            }
          }),
        )
      }
    },
    { immediate: true },
  )

  function toggleCategory(name: string) {
    ouraHubStore.searchResults = []
    ouraHubStore.showOnlyMyApps = false

    const selected = ouraHubStore.selectedCategories

    if (selected.includes(name)) {
      ouraHubStore.selectedCategories = selected.filter((c) => c !== name)
    } else {
      ouraHubStore.selectedCategories = [...selected.filter((c) => c !== 'my'), name]

      servicesStore.trackCategoryFilter(name)
    }
  }

  function executeDirectAction(event: KeyboardEvent) {
    if (ouraHubStore.searchActions.length > 0) {
      event.preventDefault()
      event.stopPropagation()

      serviceLaunch.launchService(ouraHubStore.searchActions[0])
    }
  }
</script>

<template lang="html">
  <v-textarea
    v-model="ouraHubStore.searchOrTask"
    rounded
    auto-grow
    clearable
    autofocus
    rows="1"
    max-rows="20"
    density="compact"
    variant="outlined"
    hide-details="auto"
    class="flex-grow-0 my-4"
    style="width: min(100%, 900px)"
    prepend-inner-icon="mdi-magnify"
    placeholder="Search for internal apps / tools with account uuid / account email / device serial or keywords..."
    :loading="assistantStore.isLoading"
    :hint="
      ouraHubStore.searchActions.length > 0
        ? 'Press Enter to ' + (ouraHubStore.searchActions[0].summary || 'launch the first application shown below')
        : ''
    "
    @keydown.enter="executeDirectAction($event)"
    @click:clear="ouraHubStore.searchResults = []"
    @update:model-value="(assistantStore.reset(), (ouraHubStore.searchResults = []))"
  />

  <!--
  placeholder="Search for applications / internal tools / enter account uuid / enter serial or describe your task..."
   @click:append="assistantStore.assist(ouraHubStore.searchOrTask, servicesStore.servicesList)"
    @keyup.ctrl.enter="assistantStore.assist(ouraHubStore.searchOrTask, servicesStore.servicesList)"

    <template #append-inner>
      <v-progress-circular v-if="assistantStore.isLoading" indeterminate size="20" class="mr-2" color="primary" />
      <v-tooltip v-else location="top end">
        <template #activator="{ props: tooltipProps }">
          <v-icon
            v-bind="tooltipProps"
            icon="mdi-robot"
            :color="assistantStore.actions?.length ? 'info' : ouraHubStore.searchOrTask ? 'success' : ''"
  />
        </template>

        <div v-if="ouraHubStore.searchOrTask && assistantStore.actions === null">
          🤖 Press 'Enter' or click me to let me analyze your task and help you with it!
        </div>
        <div v-else-if="ouraHubStore.searchOrTask && assistantStore.actions?.length">
          ✅ I found some actions to help with your task, select an action from the results below.
        </div>
        <div v-else-if="ouraHubStore.searchOrTask && !assistantStore.actions?.length">
          ⚠️ I couldn't find any suitable actions to help with your task, but the services listed below might help you.
        </div>
        <div v-else>💡 Describe your task and press 'Enter' or click me to get help!</div>
      </v-tooltip>
    </template>-->

  <div v-if="assistantStore.actions?.length" class="mb-8">
    <template v-for="(action, index) in assistantStore.actions" :key="index">
      <v-tooltip location="top" max-width="400">
        <template #activator="{ props: tooltipProps }">
          <o-btn-secondary
            v-bind="tooltipProps"
            size="small"
            class="mr-2"
            color="secondary"
            :text="action.action"
            :prepend-icon="action.icon"
            :disabled="assistantStore.isLoading"
            @click="assistantStore.execute(ouraHubStore.searchOrTask, action)"
          />
        </template>
        {{ action.reasoning }}
      </v-tooltip>
    </template>
  </div>
  <div v-else class="mb-10">
    <v-chip
      text="All"
      class="mr-2"
      :variant="
        !ouraHubStore.selectedCategories.length && !ouraHubStore.showOnlyMyApps && !ouraHubStore.showUnderDevel
          ? 'flat'
          : 'tonal'
      "
      @click="
        ((ouraHubStore.searchResults = []),
        (ouraHubStore.selectedCategories = []),
        (ouraHubStore.showOnlyMyApps = false),
        (ouraHubStore.showUnderDevel = false))
      "
    />

    <v-chip
      class="mr-2"
      text="My apps"
      prepend-icon="mdi-creation"
      :variant="ouraHubStore.showOnlyMyApps ? 'flat' : 'tonal'"
      @click="
        ((ouraHubStore.searchResults = []),
        (ouraHubStore.selectedCategories = []),
        (ouraHubStore.showUnderDevel = false),
        (ouraHubStore.showOnlyMyApps = !ouraHubStore.showOnlyMyApps))
      "
    />

    <template v-for="category in servicesStore.sortedCategories.slice(0, 5)" :key="category.name">
      <v-chip
        class="mr-2"
        :text="category.name"
        :prepend-icon="category.icon"
        :variant="ouraHubStore.selectedCategories.includes(category.name) ? 'flat' : 'tonal'"
        @click="toggleCategory(category.name)"
      />
    </template>

    <v-menu offset="8" location="bottom end">
      <template #activator="{ props: moreProps }">
        <v-chip
          v-bind="moreProps"
          prepend-icon="mdi-dots-vertical"
          :variant="moreCategoriesCount ? 'flat' : 'tonal'"
          :text="moreCategoriesCount ? `More (${moreCategoriesCount})` : 'More'"
        />
      </template>

      <v-list density="compact">
        <v-list-item
          v-for="moreCategory in servicesStore.sortedCategories.slice(5)"
          :key="moreCategory.name"
          :title="moreCategory.name"
          :prepend-icon="moreCategory.icon"
          :active="ouraHubStore.selectedCategories.includes(moreCategory.name)"
          @click="toggleCategory(moreCategory.name)"
        />

        <v-divider class="my-2" />

        <v-list-item
          title="Under development"
          :active="ouraHubStore.showUnderDevel"
          :prepend-icon="'mdi-account-hard-hat-outline'"
          @click="
            ((ouraHubStore.searchResults = []),
            (ouraHubStore.showOnlyMyApps = false),
            (ouraHubStore.showUnderDevel = !ouraHubStore.showUnderDevel))
          "
        />
      </v-list>
    </v-menu>
  </div>
</template>
